import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, RotateCcw, Bell } from 'lucide-react';
import React from 'react';

const PRESETS = [5, 10, 15, 25];
const fmt = (s) => { const t = Math.round(s); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; };

// Soft three-note chime (no audio file needed)
const chime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [523.25, 659.25, 783.99].forEach((hz, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = hz;
      const t = ctx.currentTime + i * 0.45;
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.18, t + 0.05); g.gain.exponentialRampToValueAtTime(0.001, t + 1.6);
      o.connect(g); g.connect(ctx.destination); o.start(t); o.stop(t + 1.7);
    });
    setTimeout(() => ctx.close(), 3500);
  } catch { /* audio unavailable */ }
  if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
};

export const minutesFrom = (v) => {
  if (!v) return null;
  if (typeof v.pick === 'number') return PRESETS[v.pick];
  const n = parseFloat(v.other);
  return n > 0 ? Math.min(n, 180) : null;
};

export default function FocusTimer({ minutes: mins, testId = 'fb-timer' }) {
  const minutes = mins ? Math.max(1, Math.round(mins * 60)) / 60 : null;
  const [left, setLeft] = useState(null);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const end = useRef(null);

  useEffect(() => { if (!running) { setLeft(minutes ? minutes * 60 : null); setDone(false); } }, [minutes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => {
      const s = Math.max(0, Math.round((end.current - Date.now()) / 1000));
      setLeft(s);
      if (s === 0) { setRunning(false); setDone(true); chime(); }
    }, 250);
    return () => clearInterval(t);
  }, [running]);

  const start = useCallback(() => { end.current = Date.now() + (left || minutes * 60) * 1000; setDone(false); setRunning(true); }, [left, minutes]);
  const reset = () => { setRunning(false); setDone(false); setLeft(minutes * 60); };

  if (!minutes) return <p className="mt-3 font-body text-xs text-stone-500 italic" data-testid={`${testId}-hint`}>Pick a focus time to start a timer.</p>;
  return (
    <div className={`fb-no-print mt-4 flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3 ${done ? 'bg-amber-50 border border-amber-200' : 'bg-[#f3ecf2]'}`} data-testid={testId}>
      <span className="font-heading text-3xl tabular-nums text-[#5a2f50] w-24" data-testid={`${testId}-display`}>{fmt(left ?? minutes * 60)}</span>
      {running ? (
        <button type="button" onClick={() => setRunning(false)} className="inline-flex items-center gap-1.5 bg-white border border-[#d9cbd6] text-[#6b3a5e] rounded-full px-4 py-1.5 font-body text-sm" data-testid={`${testId}-pause`}><Pause size={14} /> Pause</button>
      ) : (
        <button type="button" onClick={start} className="inline-flex items-center gap-1.5 bg-[#6b3a5e] text-white rounded-full px-4 py-1.5 font-body text-sm whitespace-nowrap" data-testid={`${testId}-start`}><Play size={14} /> {left && left < minutes * 60 && !done ? 'Resume' : 'Start focus'}</button>
      )}
      <button type="button" onClick={reset} className="p-2 text-stone-500 hover:text-[#6b3a5e]" aria-label="Reset" data-testid={`${testId}-reset`}><RotateCcw size={15} /></button>
      {done && <span className="basis-full inline-flex items-center gap-1 font-body text-sm text-amber-700" data-testid={`${testId}-done`}><Bell size={14} /> Time’s up — lovely focus! Take a breath.</span>}
    </div>
  );
}
